import { Controller } from "@hotwired/stimulus";
import { serverTextValue } from "./server_text_value";

class AttachmentPreviewError extends Error {}

type AttachmentState = "ready" | "uploading" | "error";

type AttachmentEntry = {
    id: string;
    file: File;
    mimeType: string;
    displayName: string;
    previewUrl: string | null;
    error: string | null;
    state: AttachmentState;
};

type PreviewOptions = {
    url: string;
    mimeType: string;
    displayName: string;
};

type AttachmentPreviewEvent = CustomEvent<Partial<PreviewOptions>>;

type HtmxAfterRequestEvent = Event & {
    detail?: {
        elt?: Element;
        xhr?: {
            status?: number;
        };
    };
};

type MaybeDisableableElement = HTMLElement & {
    disabled?: boolean;
};

const IMAGE_PREFIX = "image/";
const PDF_MIME = "application/pdf";
const CSV_MIME_TYPES = new Set(["text/csv", "application/csv", "application/x-csv"]);
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DROP_ZONE_OPEN_DURATION_MS = 140;
const DROP_ZONE_ANIMATION_ID = "ticket-chat-drop-zone-open";

export default class extends Controller<HTMLElement> {
    static targets = [
        "attachButton",
        "composerPanel",
        "countLabel",
        "dropOverlay",
        "fileInput",
        "pendingList",
        "pendingPanel",
        "previewBody",
        "previewDialog",
        "previewDownload",
        "sendButton",
        "textInput",
        "toolbarPanel",
        "voiceButton",
    ];

    static values = {
        allowedMimeTypes: String,
        countLimitReached: String,
        defaultName: String,
        emptyFileError: String,
        errorLabel: String,
        fileLabel: String,
        imageLabel: String,
        maxFiles: Number,
        maxSize: Number,
        pdfLabel: String,
        pdfLoading: String,
        pdfUnavailable: String,
        removeLabel: String,
        sendLabel: String,
        stopResponseLabel: String,
        ticketId: String,
        tooLargeError: String,
        unknownTypeLabel: String,
        unsupportedTypeLabel: String,
        uploadingLabel: String,
    };

    declare readonly attachButtonTarget: MaybeDisableableElement;
    declare readonly composerPanelTarget: HTMLElement;
    declare readonly countLabelTarget: HTMLElement;
    declare readonly dropOverlayTarget: HTMLElement;
    declare readonly fileInputTarget: HTMLInputElement;
    declare readonly pendingListTarget: HTMLElement;
    declare readonly pendingPanelTarget: HTMLElement;
    declare readonly previewBodyTarget: HTMLElement;
    declare readonly previewDialogTarget: HTMLElement;
    declare readonly previewDownloadTarget: HTMLAnchorElement;
    declare readonly hasPreviewDownloadTarget: boolean;
    declare readonly sendButtonTarget: HTMLButtonElement;
    declare readonly textInputTarget: HTMLTextAreaElement;
    declare readonly toolbarPanelTarget: HTMLElement;
    declare readonly voiceButtonTarget: HTMLButtonElement;
    declare readonly hasAttachButtonTarget: boolean;
    declare readonly hasComposerPanelTarget: boolean;
    declare readonly hasCountLabelTarget: boolean;
    declare readonly hasDropOverlayTarget: boolean;
    declare readonly hasFileInputTarget: boolean;
    declare readonly hasPendingListTarget: boolean;
    declare readonly hasPendingPanelTarget: boolean;
    declare readonly hasSendButtonTarget: boolean;
    declare readonly hasTextInputTarget: boolean;
    declare readonly hasToolbarPanelTarget: boolean;
    declare readonly hasVoiceButtonTarget: boolean;
    declare readonly allowedMimeTypesValue: string;
    declare readonly countLimitReachedValue: string;
    declare readonly defaultNameValue: string;
    declare readonly emptyFileErrorValue: string;
    declare readonly errorLabelValue: string;
    declare readonly fileLabelValue: string;
    declare readonly imageLabelValue: string;
    declare readonly maxFilesValue: number;
    declare readonly maxSizeValue: number;
    declare readonly pdfLabelValue: string;
    declare readonly pdfLoadingValue: string;
    declare readonly pdfUnavailableValue: string;
    declare readonly removeLabelValue: string;
    declare readonly sendLabelValue: string;
    declare readonly stopResponseLabelValue: string;
    declare readonly ticketIdValue: string;
    declare readonly tooLargeErrorValue: string;
    declare readonly unknownTypeLabelValue: string;
    declare readonly unsupportedTypeLabelValue: string;
    declare readonly uploadingLabelValue: string;
    declare readonly hasCountLimitReachedValue: boolean;
    declare readonly hasDefaultNameValue: boolean;
    declare readonly hasEmptyFileErrorValue: boolean;
    declare readonly hasErrorLabelValue: boolean;
    declare readonly hasFileLabelValue: boolean;
    declare readonly hasImageLabelValue: boolean;
    declare readonly hasPdfLabelValue: boolean;
    declare readonly hasPdfLoadingValue: boolean;
    declare readonly hasPdfUnavailableValue: boolean;
    declare readonly hasRemoveLabelValue: boolean;
    declare readonly hasSendLabelValue: boolean;
    declare readonly hasStopResponseLabelValue: boolean;
    declare readonly hasTooLargeErrorValue: boolean;
    declare readonly hasUnknownTypeLabelValue: boolean;
    declare readonly hasUnsupportedTypeLabelValue: boolean;
    declare readonly hasUploadingLabelValue: boolean;

    private entries: AttachmentEntry[] = [];
    private nextId = 1;
    private showToolbar = true;
    private uploadGrace = false;
    private graceTimer: number | null = null;
    private activePreviewObjectUrl: string | null = null;
    private previewRequestId = 0;
    private dropOverlayVisible = false;

    private readonly handleAttachmentPreviewOpen = (event: Event): void => {
        this.openDispatchedPreview(event);
    };

    private readonly handleDocumentLinkClick = (event: MouseEvent): void => {
        if (this.identifier !== "ticket-chat-attachments" || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
        if (!link) return;
        if (link.origin !== window.location.origin || !/^\/gestion\/documents\/[0-9a-f-]{36}\/fichier$/i.test(link.pathname)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openPreview({ url: link.href, mimeType: "", displayName: link.textContent?.trim() || this.defaultName() });
    };

    connect(): void {
        this.entries = [];
        this.nextId = 1;
        this.showToolbar =
            this.isComposerHovered() || this.composerContainsFocus() || this.isVoiceRecording() || this.hasComposerContent();
        this.uploadGrace = false;
        this.graceTimer = null;
        this.dropOverlayVisible = false;
        this.element.addEventListener("ticket-attachment-preview:open", this.handleAttachmentPreviewOpen);
        this.element.addEventListener("click", this.handleDocumentLinkClick, true);
        this.update();
    }

    sendButtonTargetConnected(): void {
        this.updateSendButton();
    }

    disconnect(): void {
        this.previewRequestId += 1;
        this.clearActivePreviewObjectUrl();
        this.element.removeEventListener("ticket-attachment-preview:open", this.handleAttachmentPreviewOpen);
        this.element.removeEventListener("click", this.handleDocumentLinkClick, true);
        this.clearGraceTimer();
        this.revokeObjectUrls();
        this.dropOverlayVisible = false;
        if (this.hasDropOverlayTarget) {
            this.dropOverlayTarget.getAnimations()
                .filter((animation) => animation.id === DROP_ZONE_ANIMATION_ID)
                .forEach((animation) => animation.cancel());
        }
    }

    ticketIdValueChanged(ticketId: string, previousTicketId: string): void {
        if (!previousTicketId || ticketId === previousTicketId) {
            return;
        }

        this.closePreview();
        this.clearEntries();
    }

    composerEnter(): void {
        this.clearGraceTimer();
        this.uploadGrace = false;
        this.showToolbar = true;
        this.updateChrome();
    }

    composerLeave(event?: Event): void {
        if (this.focusStillInsideComposer(event) || this.composerContainsFocus()) {
            return;
        }

        // Keep the toolbar visible if the user is in any active compose state:
        // - a voice recording / transcription is in flight, OR
        // - the textarea already holds typed or transcribed content. Otherwise
        // the toolbar would retract right after the transcript lands in the
        // textarea, even though the user is mid-composition.
        this.showToolbar = this.isVoiceRecording() || this.hasComposerContent();
        this.updateChrome();
    }

    recordingChanged(event: CustomEvent<{ isRecording?: boolean }>): void {
        const isRecording = Boolean(event.detail?.isRecording);
        this.element.dataset.ticketChatRecording = isRecording ? "true" : "false";
        // Keep the toolbar (paperclip + mic + status + waveform) visible during
        // the entire recording flow (starting → recording → stopping →
        // uploading) so the user can see what's happening even after the mouse
        // leaves the composer. Drop back to hover-revealed once we're idle.
        this.showToolbar = Boolean(isRecording || this.isComposerHovered() || this.composerContainsFocus() || this.hasComposerContent());
        this.updateChrome();
    }

    openPicker(event: Event): void {
        event.preventDefault();
        if (this.isAtCapacity()) {
            return;
        }
        this.openFilePicker();
    }

    labelPickerClick(event: Event): void {
        if (this.isAtCapacity()) {
            event.preventDefault();
        }
    }

    labelPickerKeydown(event: KeyboardEvent): void {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        event.preventDefault();
        if (this.isAtCapacity()) {
            return;
        }

        this.openFilePicker();
    }

    fileInputChanged(): void {
        const files = Array.from(this.fileInputTarget.files || []);
        this.fileInputTarget.value = "";
        this.addFiles(files);
    }

    paste(event: ClipboardEvent): void {
        const items = Array.from(event.clipboardData?.items || []);
        const imageFiles: File[] = [];
        const otherFiles: File[] = [];

        for (const item of items) {
            if (item.kind !== "file") {
                continue;
            }

            const file = item.getAsFile();
            if (!file) {
                continue;
            }

            if ((file.type || "").startsWith(IMAGE_PREFIX)) {
                imageFiles.push(file);
            } else {
                otherFiles.push(file);
            }
        }

        const picked: File[] = [];
        if (imageFiles.length > 0) {
            picked.push(imageFiles.find((file) => file.type === "image/png") || imageFiles[0]);
        }
        picked.push(...otherFiles);

        if (picked.length === 0) {
            return;
        }

        event.preventDefault();
        this.addFiles(picked);
    }

    dragOver(event: DragEvent): void {
        if (!this.hasFiles(event)) {
            return;
        }

        event.preventDefault();
        this.showDropOverlay();
    }

    dragLeave(event: DragEvent): void {
        if (event.relatedTarget instanceof Node && this.element.contains(event.relatedTarget)) {
            return;
        }

        this.hideDropOverlay();
    }

    drop(event: DragEvent): void {
        if (!this.hasFiles(event)) {
            return;
        }

        event.preventDefault();
        this.hideDropOverlay();
        this.addFiles(Array.from(event.dataTransfer?.files || []));
    }

    contentChanged(): void {
        this.updateSendButton();
        // When the textarea gains text (typed or voice transcript), pin the
        // toolbar so the icons + status row stay visible even if the cursor
        // wanders away from the composer. When it empties (e.g. user clears
        // the field), revert to hover-driven visibility unless the cursor is
        // currently inside the composer.
        const hasContent = this.hasComposerContent();
        if (hasContent && !this.showToolbar) {
            this.showToolbar = true;
            this.updateChrome();
        } else if (!hasContent && !this.isComposerHovered() && !this.isVoiceRecording()) {
            if (this.showToolbar) {
                this.showToolbar = false;
                this.updateChrome();
            }
        }
    }

    submit(event: Event): void {
        if (this.hasUploading()) {
            event.preventDefault();
            return;
        }

        const hasText = this.textInputTarget.value.trim().length > 0;
        const hasFiles = this.readyEntries().length > 0;

        if (!hasText && !hasFiles) {
            event.preventDefault();
            this.textInputTarget.focus();
        }
    }

    beforeRequest(event: Event): void {
        if (this.isStopRequestEvent(event) || this.readyEntries().length === 0) {
            return;
        }

        this.entries.forEach((entry) => {
            if (entry.state === "ready") {
                entry.state = "uploading";
            }
        });
        this.update();
    }

    afterRequest(event: HtmxAfterRequestEvent): void {
        if (this.isStopRequestEvent(event)) {
            return;
        }

        const status = event.detail?.xhr?.status || 0;
        if (status >= 200 && status < 300) {
            this.clearEntries();
            return;
        }

        this.restoreUploads();
    }

    openPersistedPreview(event: Event): void {
        event.preventDefault();
        const trigger = event.currentTarget;
        if (!(trigger instanceof HTMLElement)) {
            return;
        }

        this.openPreview({
            url: trigger.dataset.attachmentUrl || "",
            mimeType: trigger.dataset.attachmentMimeType || "",
            displayName: trigger.dataset.attachmentDisplayName || this.defaultName(),
        });
    }

    openPersistedPreviewFromKeyboard(event: KeyboardEvent): void {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        this.openPersistedPreview(event);
    }

    openDispatchedPreview(event: Event): void {
        event.preventDefault();
        const detail = (event as AttachmentPreviewEvent).detail || {};

        this.openPreview({
            url: detail.url || "",
            mimeType: detail.mimeType || "",
            displayName: detail.displayName || this.defaultName(),
        });
    }

    closePreview(event?: Event): void {
        event?.preventDefault();
        this.previewDialogTarget.classList.add("hidden");
        this.previewDialogTarget.classList.remove("flex");
        this.previewRequestId += 1;
        this.clearActivePreviewObjectUrl();
        this.previewBodyTarget.replaceChildren();
    }

    stopPreviewClose(event: Event): void {
        event.stopPropagation();
    }

    windowKeydown(event: KeyboardEvent): void {
        if (event.key === "Escape" && !this.previewDialogTarget.classList.contains("hidden")) {
            this.closePreview(event);
        }
    }

    private addFiles(files: File[]): void {
        const remaining = this.maxFiles() - this.entries.length;
        if (remaining <= 0) {
            return;
        }

        files.slice(0, remaining).forEach((file) => {
            const mimeType = this.resolvedMimeType(file);
            const error = this.validationError(file, mimeType);
            const previewUrl = !error && this.isPreviewable(mimeType)
                ? URL.createObjectURL(file)
                : null;
            const id = String(this.nextId++);

            this.entries.push({
                id,
                file,
                mimeType,
                displayName: file.name || this.defaultName(),
                previewUrl,
                error,
                state: error ? "error" : "ready",
            });
        });

        this.syncFileInput();
        this.update();
        this.startUploadGrace();
    }

    private removeEntry(id: string): void {
        const entry = this.entries.find((item) => item.id === id);
        if (entry?.previewUrl) {
            URL.revokeObjectURL(entry.previewUrl);
        }

        this.entries = this.entries.filter((item) => item.id !== id);
        this.syncFileInput();
        this.update();
    }

    private clearEntries(): void {
        this.clearGraceTimer();
        this.uploadGrace = false;
        this.showToolbar = false;
        this.revokeObjectUrls();
        this.entries = [];
        this.syncFileInput();
        this.update();
    }

    private revokeObjectUrls(): void {
        this.entries.forEach((entry) => {
            if (entry.previewUrl) {
                URL.revokeObjectURL(entry.previewUrl);
            }
        });
    }

    private syncFileInput(): void {
        if (!this.hasFileInputTarget) {
            return;
        }

        const dataTransfer = new DataTransfer();
        this.validEntries().forEach((entry) => dataTransfer.items.add(entry.file));
        this.fileInputTarget.files = dataTransfer.files;
    }

    private update(): void {
        this.renderEntries();
        this.updateSendButton();
        this.updateAttachButton();
        this.updateVoiceButton();
        this.updateChrome();
    }

    private renderEntries(): void {
        if (!this.hasPendingListTarget || !this.hasCountLabelTarget) {
            return;
        }

        this.pendingListTarget.replaceChildren();

        this.entries.forEach((entry) => {
            this.pendingListTarget.appendChild(this.buildChip(entry));
        });

        this.pendingListTarget.classList.toggle("max-h-[155px]", this.entries.length > 4);
        this.pendingListTarget.classList.toggle("overflow-y-auto", this.entries.length > 4);
        this.countLabelTarget.textContent = `${this.entries.length} / ${this.maxFiles()}${this.isAtCapacity() ? ` - ${this.countLimitReached()}` : ""}`;
        this.countLabelTarget.classList.toggle("text-red-600", this.isAtCapacity());
        this.countLabelTarget.classList.toggle("dark:text-red-300", this.isAtCapacity());
        this.countLabelTarget.classList.toggle("font-medium", this.isAtCapacity());
        this.hydrateIcons(this.pendingListTarget);
    }

    private buildChip(entry: AttachmentEntry): HTMLElement {
        const canOpen = !entry.error && entry.state === "ready" && this.isPreviewable(entry.mimeType);
        const chip = document.createElement(canOpen ? "button" : "div");
        chip.className = "group relative flex w-full min-w-0 items-start gap-2 rounded-lg border border-border bg-background py-2 pl-2 pr-2 text-left transition-colors";
        chip.dataset.testid = "ticket-chat-attachment-pending";
        chip.dataset.tooltip = entry.error || entry.displayName;

        if (entry.state === "error") {
            chip.classList.add("border-red-200", "dark:border-red-800", "bg-red-50", "dark:bg-red-950/40");
        }

        if (canOpen && chip instanceof HTMLButtonElement) {
            chip.type = "button";
            chip.classList.add("cursor-pointer", "hover:bg-muted");
            chip.dataset.posthogId = "tickets-chat-attachment-preview-pending";
            chip.addEventListener("click", () => {
                this.openPreview({
                    url: entry.previewUrl || "",
                    mimeType: entry.mimeType,
                    displayName: entry.displayName,
                });
            });
        }

        chip.appendChild(this.buildThumb(entry));
        chip.appendChild(this.buildText(entry));
        chip.appendChild(this.buildRemoveButton(entry.id));
        return chip;
    }

    private buildThumb(entry: AttachmentEntry): HTMLElement {
        const thumb = document.createElement("span");
        thumb.className = "flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-md";

        if (entry.state === "uploading") {
            thumb.appendChild(this.buildIcon("loader-circle", "h-4 w-4 text-muted-foreground motion-safe:animate-spin"));
            return thumb;
        }

        if (entry.state === "error") {
            thumb.appendChild(this.buildIcon("circle-alert", "h-4 w-4 text-red-500"));
            return thumb;
        }

        if (entry.mimeType.startsWith(IMAGE_PREFIX) && entry.previewUrl) {
            const img = document.createElement("img");
            img.src = entry.previewUrl;
            img.alt = entry.displayName;
            img.className = "h-full w-full object-cover";
            thumb.appendChild(img);
            return thumb;
        }

        if (entry.mimeType.startsWith(IMAGE_PREFIX)) {
            thumb.classList.add("bg-muted");
            thumb.appendChild(this.buildIcon("image", "h-4 w-4 text-muted-foreground"));
        } else if (this.normalizedMimeType(entry.mimeType) === PDF_MIME) {
            thumb.classList.add("bg-red-50", "dark:bg-red-950/40");
            thumb.appendChild(this.buildIcon("file-text", "h-4 w-4 text-red-500"));
        } else if (this.spreadsheetLabel(entry.mimeType)) {
            thumb.classList.add("bg-emerald-50", "dark:bg-emerald-950/40");
            thumb.appendChild(this.buildIcon("file-spreadsheet", "h-4 w-4 text-emerald-600 dark:text-emerald-300"));
        } else {
            thumb.classList.add("bg-muted");
            thumb.appendChild(this.buildIcon("file-text", "h-4 w-4 text-muted-foreground"));
        }

        return thumb;
    }

    private buildText(entry: AttachmentEntry): HTMLElement {
        const text = document.createElement("span");
        text.className = "min-w-0 flex-1 pt-0.5";

        const name = document.createElement("span");
        name.className = "block truncate text-[12px] font-medium text-foreground";
        name.textContent = entry.displayName;

        const sub = document.createElement("span");
        sub.className = entry.state === "error"
            ? "mt-0.5 block break-words text-xs leading-4 text-red-700 dark:text-red-300"
            : "block text-[10px] text-muted-foreground";
        sub.dataset.testid = "ticket-chat-attachment-status";
        sub.textContent = this.stateLabel(entry);

        text.append(name, sub);
        return text;
    }

    private buildRemoveButton(id: string): HTMLButtonElement {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-gray-300 text-white opacity-70 transition-opacity hover:bg-gray-400 hover:opacity-100";
        button.setAttribute("aria-label", this.removeLabel());
        button.dataset.posthogId = "tickets-chat-attachment-remove";
        button.appendChild(this.buildIcon("x", "h-2.5 w-2.5"));
        button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.removeEntry(id);
        });
        return button;
    }

    private openPreview({ url, mimeType, displayName }: PreviewOptions): void {
        if (!url) return;
        this.previewRequestId += 1;
        this.clearActivePreviewObjectUrl();
        const shell = this.buildPdfPreviewShell();
        this.previewBodyTarget.replaceChildren(shell);
        this.previewDialogTarget.classList.remove("hidden");
        this.previewDialogTarget.classList.add("flex");
        void this.loadPreview(shell, url, mimeType, displayName, this.previewRequestId);
    }

    private buildPdfPreviewShell(): HTMLElement {
        const shell = document.createElement("div");
        shell.className = "flex max-h-[80vh] w-[88vw] max-w-[1100px] items-center justify-center overflow-hidden rounded-lg bg-card shadow-2xl";
        shell.textContent = this.pdfLoading();
        return shell;
    }

    private async loadPreview(shell: HTMLElement, url: string, mimeType: string, displayName: string, requestId: number): Promise<void> {
        try {
            const response = await this.previewResponse(url);
            const blob = await response.blob();
            if (requestId !== this.previewRequestId) return;
            const responseType = this.normalizedMimeType(blob.type);
            // A login redirect is not a document, even when it returns HTTP 200.
            if (responseType === "text/html") throw new Error("Attachment unavailable");
            const disposition = response.headers.get("content-disposition") || "";
            const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
            const filename = encodedFilename ? decodeURIComponent(encodedFilename) : displayName;
            const declaredType = this.normalizedMimeType(mimeType);
            const type = this.resolvedMimeType({
                name: filename,
                type: responseType && responseType !== "application/octet-stream" ? responseType
                    : declaredType === "application/octet-stream" ? "" : declaredType,
            });
            this.activePreviewObjectUrl = URL.createObjectURL(type && type !== responseType ? new Blob([blob], { type }) : blob);
            if (this.hasPreviewDownloadTarget) {
                this.previewDownloadTarget.href = this.activePreviewObjectUrl;
                this.previewDownloadTarget.download = filename;
                // Native PDF viewers already provide a download control.
                this.previewDownloadTarget.classList.toggle("hidden", type === PDF_MIME && navigator.pdfViewerEnabled);
            }
            if (type === PDF_MIME) {
                shell.replaceChildren(this.buildPdfFrame(this.activePreviewObjectUrl, filename));
                shell.classList.remove("items-center", "justify-center");
            } else if (type.startsWith(IMAGE_PREFIX)) {
                const img = document.createElement("img");
                img.src = this.activePreviewObjectUrl;
                img.alt = filename;
                img.className = "max-h-[80vh] max-w-[88vw] rounded-md object-contain shadow-2xl";
                shell.replaceWith(img);
            } else if (type === "text/plain" || CSV_MIME_TYPES.has(type)) {
                const text = await blob.text();
                if (requestId !== this.previewRequestId) return;
                const content = document.createElement("pre");
                content.className = "max-h-[80vh] w-full overflow-auto p-6 text-sm text-foreground";
                content.setAttribute("aria-label", filename);
                content.textContent = text;
                shell.replaceChildren(content);
            } else {
                shell.classList.add("p-6", "text-sm");
                shell.textContent = filename;
            }
        } catch (error) {
            if (requestId === this.previewRequestId) this.showPreviewError(shell, error);
        }
    }

    private showPreviewError(shell: HTMLElement, error: unknown): void {
        shell.setAttribute("role", "alert");
        shell.classList.add("p-6", "text-sm", "whitespace-normal");
        shell.textContent = error instanceof AttachmentPreviewError ? error.message : this.pdfUnavailable();
    }

    private async previewResponse(url: string): Promise<Response> {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) {
            if (response.headers.get("content-type")?.includes("application/json")) {
                const body: unknown = await response.json();
                if (typeof body === "object" && body !== null && "error" in body
                    && body.error === "property_document_attachment_unavailable"
                    && "message" in body && typeof body.message === "string") {
                    throw new AttachmentPreviewError(body.message);
                }
            }
            throw new Error("Attachment unavailable");
        }
        return response;
    }

    private buildPdfFrame(url: string, displayName: string): HTMLIFrameElement {
        const frame = document.createElement("iframe");
        frame.src = url;
        frame.title = displayName;
        frame.className = "h-[80vh] w-full border-0 bg-card";
        frame.dataset.pdfFrame = "true";
        return frame;
    }

    private updateSendButton(): void {
        if (!this.hasSendButtonTarget) {
            return;
        }

        if (this.sendButtonTarget.dataset.ticketChatStopButton === "true") {
            this.sendButtonTarget.disabled = false;
            this.sendButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
            this.sendButtonTarget.dataset.tooltip = this.stopResponseLabel();
            return;
        }

        const hasText = this.hasTextInputTarget && this.textInputTarget.value.trim().length > 0;
        this.sendButtonTarget.disabled = this.hasUploading() || (!hasText && this.readyEntries().length === 0);
        this.sendButtonTarget.classList.toggle("opacity-50", this.sendButtonTarget.disabled);
        this.sendButtonTarget.classList.toggle("cursor-not-allowed", this.sendButtonTarget.disabled);
        this.sendButtonTarget.dataset.tooltip = this.hasUploading() ? this.uploadingLabel() : this.sendLabel();
    }

    private updateAttachButton(): void {
        if (!this.hasAttachButtonTarget) {
            return;
        }

        const atCapacity = this.isAtCapacity();
        if ("disabled" in this.attachButtonTarget) {
            this.attachButtonTarget.disabled = atCapacity;
        }
        this.attachButtonTarget.setAttribute("aria-disabled", atCapacity ? "true" : "false");
        this.attachButtonTarget.classList.toggle("pointer-events-none", atCapacity);
        this.attachButtonTarget.classList.toggle("opacity-40", atCapacity);
        this.attachButtonTarget.classList.toggle("cursor-not-allowed", atCapacity);
        this.attachButtonTarget.classList.toggle("cursor-pointer", !atCapacity);
    }

    private updateVoiceButton(): void {
        if (!this.hasVoiceButtonTarget) {
            return;
        }

        this.voiceButtonTarget.disabled = this.hasUploading();
    }

    private updateChrome(): void {
        this.updatePendingPanelVisibility();
        this.updateToolbarVisibility();
    }

    private updatePendingPanelVisibility(): void {
        if (!this.hasPendingPanelTarget) {
            return;
        }

        const hasEntries = this.entries.length > 0;
        const visible = hasEntries && (this.showToolbar || this.hasUploading() || this.uploadGrace || this.isVoiceRecording());

        this.pendingPanelTarget.classList.toggle("hidden", !hasEntries);
        this.pendingPanelTarget.classList.toggle("opacity-100", visible);
        this.pendingPanelTarget.classList.toggle("mb-2", visible);
        this.pendingPanelTarget.classList.toggle("translate-y-0", visible);
        this.pendingPanelTarget.classList.toggle("max-h-0", hasEntries && !visible);
        this.pendingPanelTarget.classList.toggle("opacity-0", hasEntries && !visible);
        this.pendingPanelTarget.classList.toggle("mb-0", hasEntries && !visible);
        this.pendingPanelTarget.classList.toggle("-translate-y-2", hasEntries && !visible);
        this.pendingPanelTarget.classList.toggle("overflow-hidden", hasEntries && !visible);
    }

    private updateToolbarVisibility(): void {
        if (!this.hasToolbarPanelTarget) {
            return;
        }

        const visible = this.showToolbar || this.isVoiceRecording();

        this.toolbarPanelTarget.classList.toggle("max-h-48", visible);
        this.toolbarPanelTarget.classList.toggle("overflow-visible", visible);
        this.toolbarPanelTarget.classList.toggle("opacity-100", visible);
        this.toolbarPanelTarget.classList.toggle("mb-3", visible);
        this.toolbarPanelTarget.classList.toggle("translate-y-0", visible);
        this.toolbarPanelTarget.classList.toggle("max-h-0", !visible);
        this.toolbarPanelTarget.classList.toggle("overflow-hidden", !visible);
        this.toolbarPanelTarget.classList.toggle("opacity-0", !visible);
        this.toolbarPanelTarget.classList.toggle("mb-0", !visible);
        this.toolbarPanelTarget.classList.toggle("-translate-y-2", !visible);
    }

    private isVoiceRecording(): boolean {
        return this.element.dataset.ticketChatRecording === "true";
    }

    private isComposerHovered(): boolean {
        return this.hasComposerPanelTarget && this.composerPanelTarget.matches(":hover");
    }

    private focusStillInsideComposer(event?: Event): boolean {
        if (!(event instanceof FocusEvent) || !this.hasComposerPanelTarget) {
            return false;
        }

        return event.relatedTarget instanceof Node && this.composerPanelTarget.contains(event.relatedTarget);
    }

    private composerContainsFocus(): boolean {
        return (
            this.hasComposerPanelTarget &&
            document.activeElement instanceof Node &&
            this.composerPanelTarget.contains(document.activeElement)
        );
    }

    private hasComposerContent(): boolean {
        if (!this.hasTextInputTarget) return false;
        return this.textInputTarget.value.trim().length > 0;
    }

    private readyEntries(): AttachmentEntry[] {
        return this.entries.filter((entry) => !entry.error && entry.state === "ready");
    }

    private validEntries(): AttachmentEntry[] {
        return this.entries.filter((entry) => !entry.error);
    }

    private hasUploading(): boolean {
        return this.entries.some((entry) => entry.state === "uploading");
    }

    private isAtCapacity(): boolean {
        return this.entries.length >= this.maxFiles();
    }

    private maxFiles(): number {
        return Number.isFinite(this.maxFilesValue) && this.maxFilesValue > 0
            ? this.maxFilesValue
            : 10;
    }

    private maxSize(): number {
        return Number.isFinite(this.maxSizeValue) && this.maxSizeValue > 0
            ? this.maxSizeValue
            : 50 * 1024 * 1024;
    }

    private allowedMimeTypes(): string[] {
        return (this.allowedMimeTypesValue || "")
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);
    }

    private validationError(file: File, mimeType: string): string | null {
        if (!this.allowedMimeTypes().includes(mimeType)) {
            return this.unsupportedTypeLabel();
        }

        if (file.size === 0) {
            return this.emptyFileError();
        }

        if (file.size > this.maxSize()) {
            return this.tooLargeError();
        }

        return null;
    }

    private resolvedMimeType(file: Pick<File, "name" | "type">): string {
        if (file.type) {
            return file.type;
        }

        const extension = (file.name || "").toLowerCase().split(".").pop() || "";
        const byExtension: Record<string, string> = {
            png: "image/png",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            gif: "image/gif",
            webp: "image/webp",
            pdf: "application/pdf",
            csv: "text/csv",
            txt: "text/plain",
            xlsx: XLSX_MIME,
        };

        return byExtension[extension] || "application/octet-stream";
    }

    private isPreviewable(mimeType: string): boolean {
        const normalizedMimeType = this.normalizedMimeType(mimeType);
        return normalizedMimeType.startsWith(IMAGE_PREFIX) || normalizedMimeType === PDF_MIME
            || normalizedMimeType === "text/plain" || CSV_MIME_TYPES.has(normalizedMimeType);
    }

    private kindLabel(mimeType: string): string {
        const normalizedMimeType = this.normalizedMimeType(mimeType);
        if (normalizedMimeType.startsWith(IMAGE_PREFIX)) {
            return this.imageLabel();
        }
        if (normalizedMimeType === PDF_MIME) {
            return this.pdfLabel();
        }
        const spreadsheetLabel = this.spreadsheetLabel(normalizedMimeType);
        if (spreadsheetLabel) {
            return spreadsheetLabel;
        }
        return this.fileLabel();
    }

    private spreadsheetLabel(mimeType: string): "CSV" | "XLSX" | null {
        const normalizedMimeType = this.normalizedMimeType(mimeType);
        if (CSV_MIME_TYPES.has(normalizedMimeType)) {
            return "CSV";
        }
        return normalizedMimeType === XLSX_MIME ? "XLSX" : null;
    }

    private normalizedMimeType(mimeType: string): string {
        return mimeType.trim().toLowerCase().split(";")[0] || "";
    }

    private clearActivePreviewObjectUrl(): void {
        if (this.hasPreviewDownloadTarget) {
            this.previewDownloadTarget.classList.add("hidden");
            this.previewDownloadTarget.removeAttribute("href");
            this.previewDownloadTarget.removeAttribute("download");
        }
        if (!this.activePreviewObjectUrl) {
            return;
        }

        URL.revokeObjectURL(this.activePreviewObjectUrl);
        this.activePreviewObjectUrl = null;
    }

    private stateLabel(entry: AttachmentEntry): string {
        if (entry.state === "uploading") {
            return this.uploadingLabel();
        }
        if (entry.state === "error") {
            return entry.error || this.errorLabel();
        }
        return this.kindLabel(entry.mimeType);
    }

    private countLimitReached(): string {
        return this.textValue(this.hasCountLimitReachedValue, this.countLimitReachedValue, "countLimitReached", "limit reached");
    }

    private defaultName(): string {
        return this.textValue(this.hasDefaultNameValue, this.defaultNameValue, "defaultName", "attachment");
    }

    private emptyFileError(): string {
        return this.textValue(this.hasEmptyFileErrorValue, this.emptyFileErrorValue, "emptyFileError", "Empty file");
    }

    private errorLabel(): string {
        return this.textValue(this.hasErrorLabelValue, this.errorLabelValue, "errorLabel", "Error");
    }

    private fileLabel(): string {
        return this.textValue(this.hasFileLabelValue, this.fileLabelValue, "fileLabel", "File");
    }

    private imageLabel(): string {
        return this.textValue(this.hasImageLabelValue, this.imageLabelValue, "imageLabel", "Image");
    }

    private pdfLabel(): string {
        return this.textValue(this.hasPdfLabelValue, this.pdfLabelValue, "pdfLabel", "PDF");
    }

    private pdfLoading(): string {
        return this.textValue(this.hasPdfLoadingValue, this.pdfLoadingValue, "pdfLoading", "Loading PDF...");
    }

    private pdfUnavailable(): string {
        return this.textValue(this.hasPdfUnavailableValue, this.pdfUnavailableValue, "pdfUnavailable", "PDF preview unavailable");
    }

    private removeLabel(): string {
        return this.textValue(this.hasRemoveLabelValue, this.removeLabelValue, "removeLabel", "Remove attachment");
    }

    private sendLabel(): string {
        return this.textValue(this.hasSendLabelValue, this.sendLabelValue, "sendLabel", "Send");
    }

    private stopResponseLabel(): string {
        return this.textValue(this.hasStopResponseLabelValue, this.stopResponseLabelValue, "stopResponseLabel", "Stop response");
    }

    private tooLargeError(): string {
        return this.textValue(this.hasTooLargeErrorValue, this.tooLargeErrorValue, "tooLargeError", "File too large");
    }

    private unsupportedTypeLabel(): string {
        return this.textValue(
            this.hasUnsupportedTypeLabelValue,
            this.unsupportedTypeLabelValue,
            "unsupportedTypeLabel",
            "Unsupported type",
        );
    }

    private uploadingLabel(): string {
        return this.textValue(this.hasUploadingLabelValue, this.uploadingLabelValue, "uploadingLabel", "Upload in progress...");
    }

    private textValue(hasValue: boolean, value: string, name: string, fallback: string): string {
        return serverTextValue({
            controller: this.identifier,
            fallback,
            hasValue,
            name,
            value,
        });
    }

    private startUploadGrace(): void {
        if (this.showToolbar || this.entries.length === 0) {
            return;
        }

        this.clearGraceTimer();
        this.uploadGrace = true;
        this.updateChrome();
        this.graceTimer = window.setTimeout(() => {
            this.uploadGrace = false;
            this.graceTimer = null;
            this.updateChrome();
        }, 3000);
    }

    private clearGraceTimer(): void {
        if (this.graceTimer) {
            window.clearTimeout(this.graceTimer);
            this.graceTimer = null;
        }
    }

    private restoreUploads(): void {
        let changed = false;
        this.entries.forEach((entry) => {
            if (entry.state !== "uploading") {
                return;
            }

            entry.state = "ready";
            changed = true;
        });

        if (changed) {
            this.update();
        }
    }

    private openFilePicker(): void {
        if (typeof this.fileInputTarget.showPicker === "function") {
            this.fileInputTarget.showPicker();
            return;
        }

        this.fileInputTarget.click();
    }

    private buildIcon(name: string, className: string): SVGElement {
        if (typeof window.elronCreateLucideIcon === "function") {
            return window.elronCreateLucideIcon(name, className);
        }

        const iconName = String(name || "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "circle";
        const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        const use = document.createElementNS("http://www.w3.org/2000/svg", "use");

        icon.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        icon.setAttribute("width", "24");
        icon.setAttribute("height", "24");
        icon.setAttribute("viewBox", "0 0 24 24");
        icon.setAttribute("fill", "none");
        icon.setAttribute("stroke", "currentColor");
        icon.setAttribute("stroke-width", "2");
        icon.setAttribute("stroke-linecap", "round");
        icon.setAttribute("stroke-linejoin", "round");
        icon.setAttribute("class", ["lucide", "lucide-" + iconName, className || ""].filter(Boolean).join(" "));
        icon.setAttribute("aria-hidden", "true");
        use.setAttribute("href", "#" + iconName);
        icon.appendChild(use);

        return icon;
    }

    private hydrateIcons(_root: HTMLElement): void {
        // Icons are created as SVG sprite references now, so no hydration pass is needed.
    }

    private hasFiles(event: DragEvent): boolean {
        return Array.from(event.dataTransfer?.types || []).includes("Files");
    }

    private showDropOverlay(): void {
        if (!this.hasDropOverlayTarget || this.dropOverlayVisible) {
            return;
        }

        this.dropOverlayVisible = true;
        this.dropOverlayTarget.classList.remove("hidden");
        this.dropOverlayTarget.classList.add("flex");
        this.dropOverlayTarget.setAttribute("aria-hidden", "false");
        this.dropOverlayTarget.dataset.dropZoneActive = "true";

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        this.dropOverlayTarget.getAnimations()
            .filter((animation) => animation.id === DROP_ZONE_ANIMATION_ID)
            .forEach((animation) => animation.cancel());
        const animation = this.dropOverlayTarget.animate(
            [
                { opacity: 0.72, transform: "scale(0.985)" },
                { opacity: 1, transform: "scale(1)" },
            ],
            {
                duration: DROP_ZONE_OPEN_DURATION_MS,
                easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            }
        );
        animation.id = DROP_ZONE_ANIMATION_ID;
        this.element.dispatchEvent(
            new CustomEvent("ticket-chat-drop-zone-motion", {
                bubbles: true,
                detail: {
                    duration: DROP_ZONE_OPEN_DURATION_MS,
                    type: "open",
                },
            })
        );
    }

    private hideDropOverlay(): void {
        if (!this.hasDropOverlayTarget) {
            return;
        }

        this.dropOverlayVisible = false;
        this.dropOverlayTarget.getAnimations()
            .filter((animation) => animation.id === DROP_ZONE_ANIMATION_ID)
            .forEach((animation) => animation.cancel());
        this.dropOverlayTarget.classList.add("hidden");
        this.dropOverlayTarget.classList.remove("flex");
        this.dropOverlayTarget.setAttribute("aria-hidden", "true");
        delete this.dropOverlayTarget.dataset.dropZoneActive;
    }

    private isStopRequestEvent(event: HtmxAfterRequestEvent): boolean {
        const trigger = event.detail?.elt || event.target;

        return trigger instanceof HTMLElement && trigger.dataset.ticketChatStopButton === "true";
    }
}
